import {
  type ActivityEntry,
  activityRolesFor,
  isStaffRole,
  STAFF_ROLES,
  type StaffRole,
} from '@atlas-control/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useCallback, useState } from 'react';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  activityCsvUrl,
  type ActivityFilters,
  activityQuery,
  activitySummaryQuery,
} from '@/features/staff/api';
import { actionLabel, ACTION_LABELS } from '@/lib/audit-labels';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useMe } from '@/lib/auth';
import { guard } from '@/lib/guards';
import { pageSearch } from '@/lib/page-search';

const Search = z.object({
  actorRole: z.enum(STAFF_ROLES).optional(),
  actorId: z.string().optional(),
  action: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: pageSearch,
});

export const Route = createFileRoute('/_app/activity')({
  beforeLoad: ({ context }) => guard(context.me, 'activity.read'),
  validateSearch: Search,
  component: ActivityPage,
});

const ALL = '__all__';

const ROLE_LABELS: Record<StaffRole, string> = {
  auditor: 'Auditor',
  admin: 'Admin',
  developer: 'Developer',
};

/** Search params hold dates (yyyy-mm-dd); the API wants ISO instants in the viewer's zone. */
function toApiFilters(s: z.infer<typeof Search>): ActivityFilters {
  return {
    actorRole: s.actorRole,
    actorId: s.actorId,
    action: s.action,
    from: s.from ? new Date(`${s.from}T00:00:00`).toISOString() : undefined,
    to: s.to ? new Date(`${s.to}T23:59:59.999`).toISOString() : undefined,
  };
}

function ActivityPage() {
  const { user } = useMe();
  const visibleRoles = activityRolesFor(user.role);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = toApiFilters(search);
  const summary = useQuery(activitySummaryQuery);
  const people = (summary.data ?? []).filter(
    (p) => !search.actorRole || p.role === search.actorRole,
  );
  const feed = useQuery(activityQuery(filters, search.page ?? 1));
  const [selected, setSelected] = useState<ActivityEntry | null>(null);

  const setFilter = (patch: Partial<z.infer<typeof Search>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: undefined }) });
  const setPage = useCallback(
    (n: number) => void navigate({ search: (prev) => ({ ...prev, page: n > 1 ? n : undefined }) }),
    [navigate],
  );
  const entries = feed.data?.entries ?? [];

  return (
    <>
      <PageHeader
        title="Activity"
        actions={
          <Button variant="outline" asChild>
            <a href={activityCsvUrl(filters)} download>
              <Download /> Export CSV
            </a>
          </Button>
        }
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {summary.isPending &&
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)}
        {summary.isSuccess && people.length === 0 && (
          <p className="text-sm text-muted-foreground">No staff accounts yet.</p>
        )}
        {people.map((a) => (
          <button
            key={a.actorId}
            type="button"
            onClick={() =>
              setFilter({ actorId: search.actorId === a.actorId ? undefined : a.actorId })
            }
            className="text-left"
          >
            <Card
              className={
                search.actorId === a.actorId
                  ? 'ring-2 ring-ring'
                  : 'transition-colors hover:bg-accent/50'
              }
            >
              <CardContent className="space-y-1">
                <p className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-medium">{a.name}</span>
                  {visibleRoles.length > 1 && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {ROLE_LABELS[a.role]}
                    </span>
                  )}
                </p>
                <p className="text-sm">
                  <span className="text-lg font-semibold tabular-nums">{a.actionsThisWeek}</span>{' '}
                  <span className="text-muted-foreground">actions this week</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {a.entitiesTouchedThisWeek} records touched · last active{' '}
                  {formatRelative(a.lastActiveAt)}
                </p>
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        {visibleRoles.length > 1 && (
          <div className="space-y-1">
            <Label htmlFor="f-role">Role</Label>
            <Select
              value={search.actorRole ?? ALL}
              onValueChange={(v) =>
                setFilter({
                  actorRole: v === ALL ? undefined : (v as StaffRole),
                  // A person outside the new role would leave the feed empty.
                  actorId: undefined,
                })
              }
            >
              <SelectTrigger id="f-role" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All roles</SelectItem>
                {visibleRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="f-person">Person</Label>
          <Select
            value={search.actorId ?? ALL}
            onValueChange={(v) => setFilter({ actorId: v === ALL ? undefined : v })}
          >
            <SelectTrigger id="f-person" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Everyone</SelectItem>
              {people.map((a) => (
                <SelectItem key={a.actorId} value={a.actorId}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-action">Action</Label>
          <Select
            value={search.action ?? ALL}
            onValueChange={(v) => setFilter({ action: v === ALL ? undefined : v })}
          >
            <SelectTrigger id="f-action" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All actions</SelectItem>
              {Object.entries(ACTION_LABELS).map(([code, label]) => (
                <SelectItem key={code} value={code}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-from">From</Label>
          <Input
            id="f-from"
            type="date"
            className="w-40"
            value={search.from ?? ''}
            onChange={(e) => setFilter({ from: e.target.value || undefined })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-to">To</Label>
          <Input
            id="f-to"
            type="date"
            className="w-40"
            value={search.to ?? ''}
            onChange={(e) => setFilter({ to: e.target.value || undefined })}
          />
        </div>
        {(search.actorRole || search.actorId || search.action || search.from || search.to) && (
          <Button variant="ghost" onClick={() => void navigate({ search: {} })}>
            Clear filters
          </Button>
        )}
      </div>

      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Time</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>On</TableHead>
              <TableHead>Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feed.isPending &&
              Array.from({ length: 5 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-5" />
                  </TableCell>
                </TableRow>
              ))}
            {feed.isSuccess && entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No activity matches these filters.
                </TableCell>
              </TableRow>
            )}
            {entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDateTime(e.at)}
                </TableCell>
                <TableCell>
                  {e.actor?.name ?? 'System'}
                  {e.actor && visibleRoles.length > 1 && isStaffRole(e.actor.role) && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {ROLE_LABELS[e.actor.role]}
                    </span>
                  )}
                </TableCell>
                <TableCell>{actionLabel(e.action)}</TableCell>
                <TableCell className="font-mono text-xs">
                  {e.entityType} {e.entityId.slice(0, 8)}
                </TableCell>
                <TableCell>
                  {e.before !== null || e.after !== null ? (
                    <Button variant="link" className="h-auto p-0" onClick={() => setSelected(e)}>
                      View change
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Pagination info={feed.data} onPageChange={setPage} />

      <ChangeSheet entry={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function ChangeSheet({ entry, onClose }: { entry: ActivityEntry | null; onClose: () => void }) {
  return (
    <Sheet open={entry !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        {entry && (
          <>
            <SheetHeader>
              <SheetTitle>{actionLabel(entry.action)}</SheetTitle>
              <SheetDescription>
                {entry.actor?.name ?? 'System'} · {formatDateTime(entry.at)}
              </SheetDescription>
            </SheetHeader>
            <div className="px-4 pb-6">
              <FieldDiff before={entry.before} after={entry.after} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** Field-by-field before → after for flat objects; anything nested is shown formatted. */
function FieldDiff({ before, after }: { before: unknown; after: unknown }) {
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])];
  const show = (v: unknown) =>
    v === undefined || v === null
      ? '-'
      : typeof v === 'object'
        ? JSON.stringify(v, null, 2)
        : String(v);

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-2 font-medium">Field</th>
          <th className="py-2 font-medium">Before</th>
          <th className="py-2 font-medium">After</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {keys.map((k) => (
          <tr key={k} className={show(b[k]) !== show(a[k]) ? 'bg-warning-soft/40' : undefined}>
            <td className="py-2 pr-2 align-top font-medium">{k}</td>
            <td className="py-2 pr-2 align-top whitespace-pre-wrap break-words">{show(b[k])}</td>
            <td className="py-2 align-top whitespace-pre-wrap break-words">{show(a[k])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
